import React from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import Assignees from '@/constants/Assignees';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useQueryProvider } from '@/modules/QueryProvider';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { fetcher } from '@/utils/services';
import laggy from '@/utils/swr/laggy';
import { TaskStatusEnum } from '../tasksConfig';

export const TasksContext = React.createContext();

export function useTasksContext() {
  return React.useContext(TasksContext);
}

export default function TasksProvider({ children }) {
  const { query, updateQuery } = useQueryProvider();
  const ownerOrgId = useOwnerOrgId();
  const { constituentId } = useParams();
  const key = React.useMemo(() => {
    if (!ownerOrgId) return null;
    const { status, userId: assignee, ...rest } = query;

    let isCompleted;
    if (status && !isCompleted) {
      switch (status) {
        case TaskStatusEnum.COMPLETE:
          isCompleted = true;
          break;
        case TaskStatusEnum.INCOMPLETE:
          isCompleted = false;
          break;
        case TaskStatusEnum.ALL:
        default:
          break;
      }
    }

    let userId;
    let hasAssignees;
    switch (assignee) {
      case Assignees.ALL:
        break;
      case Assignees.ASSIGNED:
        hasAssignees = true;
        break;
      case Assignees.UNASSIGNED:
        hasAssignees = false;
        break;
      default:
        userId = assignee;
        break;
    }

    return `tasks?${joinNonEmptyParams({
      constituentId,
      hasAssignees,
      isCompleted,
      ownerOrgId,
      userId,
      ...rest,
    })}`;
  }, [constituentId, ownerOrgId, query]);

  const { data, error, isLagging, isLoading } = useSWR(key, fetcher, {
    use: [laggy],
  });

  return (
    <TasksContext.Provider
      value={{
        data: data?.tasks,
        error,
        isLagging,
        isLoading,
        key,
        ownerOrgId,
        query,
        totalHits: data?.totalHits,
        updateQuery,
      }}
    >
      {children}
    </TasksContext.Provider>
  );
}

TasksProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
